import { useCallback, useEffect, useMemo, useState } from "react";
import { useApiOperation } from "src/api/hooks";
import { deleteProductsAsync } from "src/api/product/product-api";
import { ProductAddModel, ProductModel } from "src/api/product/product-types";
import { BaseButton, BaseModal, BaseTooltip } from "src/components";

import useNavigate from "src/hooks/usePartnerNavigate";
import PagePath from "src/pagePath.json";
import { useToast } from "src/recoil/toast/hook";

import { getProviderList } from "src/api/provider/provider-api";
import BuildingAndHoTable from "../../modalPopup/table/BuildingAndHoTable";
import {
  BuildingFloorAndHo,
  getPgcodeLabel,
  Modal,
  productStatusOptions,
  ProductTypeLabels,
  ProductTypes,
  salePeriodTypeOptions,
} from "../../product-types";

import { getProviderName, isPrimaryBuildingChecked, makeBuilding } from "../../forms/basicInfoForm/libs";

import moment from "moment";
import { ServiceTypes } from "src/api/adminMemo/adminmemo-types";
import AdminMemoV3 from "src/components/AdminMemoV3/AdminMemoV3";
import GoToListButton from "src/components/GoToListButton";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import Link from "src/components/PartnerLink";
import RegistrationInformation from "src/components/RegistrationInformation";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import UsageApplicationProductForm from "src/pages/contract/form/components/application/component/UsageApplicationProductForm";
import { useModal } from "src/recoil/modalState/hook";
import { YmdFormat } from "src/utils/common-util";
import useUsageApplication from "../../hooks/useUsageApplication";
import { columns } from "./columns";
import ContractAvailModal from "./ContractAvailModal";

export type ProductFormData = {
  providerId: ProductAddModel["providerId"];
  productName: ProductAddModel["productName"];
  introduce: ProductAddModel["introduce"];
  buildingList: Array<BuildingFloorAndHo>;
  productType: ProductAddModel["productType"];
  status?: ProductAddModel["status"];
};

type Props = {
  product: ProductModel;
  productId: string;
};

/* 
  공간상품 등록 > 상세 > 기본 정보
 */
const BasicInfoDetail = ({ product: productDetail, productId }: Props) => {
  const { isAuthority } = usePartnerAuthority();

  const { openToast } = useToast();
  const navigate = useNavigate();
  const { setBaseModal, setAbstractModalZ1 } = useModal();
  const taapSpaceUrl = useMemo(() => process.env.REACT_APP_COURT_BASSEURL, []);

  const [selectedRow, setSelectedRow] = useState({});
  const [buildingAndHo, setBuildingAndHo] = useState<Array<BuildingFloorAndHo>>([]); // 테이블용

  // 모달 useState
  const [deleteModal, setDeleteModal] = useState<Modal>({ isOpen: false });

  const [selectedProvider, setSelectedProvider] = useState<{
    providerId: string;
    uid: string;
    providerName: string;
  }>();

  //상품 링크 주소
  const t_product_url = taapSpaceUrl + "/front/court/product/" + productId;
  // 공간상품 상세 삭제 api
  const { executeAsync: deleteProductDetail } = useApiOperation(deleteProductsAsync);

  //  프로바이더 목록 api
  const { executeAsync: getProviers } = useApiOperation(getProviderList);

  const { getPrApplication } = useUsageApplication();

  /** 디폴트 벨류 초기화, 기본 정보 셋팅 */
  useEffect(() => {
    if (productId && productDetail?.buildingList) {
      const newBuilding = makeBuilding(productDetail["buildingList"]);

      setBuildingAndHo(newBuilding);
      isPrimaryBuildingChecked(newBuilding, setSelectedRow, productId);
    }
  }, [productDetail, productId]);

  /** 프로바이더명 조회 후 setState */
  useEffect(() => {
    if (productId && productDetail?.providerId) {
      getProviderName(productDetail.providerId, getProviers).then((data) => setSelectedProvider(data));
    }
  }, [getProviers, productDetail, productId]);

  const onDeleteProductDetail = useCallback(async () => {
    if (deleteModal.type === "PRODUCT_DELETE") {
      // 상품 삭제
      await deleteProductDetail({ productId });
      openToast({
        content: `정상적으로 삭제 되었습니다.`,
      });
      navigate(PagePath.product.list);
    }
  }, [deleteModal.type, deleteProductDetail, navigate, openToast, productId]);

  const handleCopyClipBoard = async () => {
    try {
      await navigator.clipboard.writeText(t_product_url);
      openToast({ content: "주소가 복사되었습니다" });
    } catch (err) {
      console.log(err);
      openToast({ content: "문제가 발생했습니다" });
    }
  };

  // 이용신청서 모달 오픈
  const openApplicationModal = async () => {
    const productApplication = await getPrApplication({ productId });
    setAbstractModalZ1({
      isOpen: true,
      children: (
        <UsageApplicationProductForm //
          onClose={() => setAbstractModalZ1({ isOpen: false })}
          product={productDetail}
          {...(productApplication?.applicationForm && {
            productApplication: {
              isUseApplication: productDetail.isUseApplication ?? false,
              formData: productApplication?.applicationForm?.formData,
            },
          })}
        />
      ),
    });
  };

  // 분을 일/시/분 문자열로 변환하는 공통 함수
  const formatMinutesToText = useCallback((totalMinutes: number | undefined | null): string => {
    if (!totalMinutes || totalMinutes === 0) return "";

    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    const parts = [];
    if (days > 0) parts.push(`${days}일`);
    if (hours > 0) parts.push(`${hours}시간`);
    if (minutes > 0) parts.push(`${minutes}분`);

    return parts.length > 0 ? parts.join(" ") : "";
  }, []);

  // 이용시작일시 표시용
  const memberCancelableOffsetText = useMemo(() => {
    if (!productDetail.isMemberCancelable) return "-";

    const formattedText = formatMinutesToText(productDetail.memberCancelableMinutes);
    return formattedText ? `${formattedText} 전까지 취소 가능` : "-";
  }, [productDetail.isMemberCancelable, productDetail.memberCancelableMinutes, formatMinutesToText]);

  // 결제기한 표시용
  const paymentDueOffsetText = useMemo(() => {
    if (!productDetail.paymentDueMinutes) return "-";
    const formattedText = formatMinutesToText(productDetail.paymentDueMinutes);
    return formattedText ? `계약 체결 후 ${formattedText} 이내` : "-";
  }, [productDetail.paymentDueMinutes, formatMinutesToText]);
  return (
    <>
      <div className="contents-container__scroll">
        <div className="contents-container__wrap">
          <div className="contents-container__wrap-contents">
            <BaseSectionTitle title={"기본정보"} />

            {/* 프로바이더 */}
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p className="">프로바이더</p>
              </div>
              <div className="contents-container__grid-contents">
                <div className="minmax400">
                  <Link
                    to={PagePath.provider.detail.replace(":id", productId ? productDetail?.providerId! : selectedProvider?.providerId!)}
                    target="_blank"
                    className={"text-underline"}
                  >
                    <span className="">{selectedProvider?.providerName}</span>
                    <span className="ic-target-blank"></span>
                  </Link>
                </div>
              </div>
            </section>

            {/* 상품 유형 */}
            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p className="">상품 유형</p>
              </div>
              <div className="contents-container__grid-contents">
                <div className="minmax400">
                  <span className="font14">{ProductTypeLabels[productDetail.productType] ?? "-"}</span>
                </div>
              </div>
            </section>
            {productDetail.productType !== ProductTypes.DIGITAL_PRODUCT && (
              <>
                {/* 건물/호실 */}
                <section className="contents-container__grid">
                  <div className="contents-container__grid-index">
                    <p className="">건물/호실</p>
                  </div>
                </section>
                {/*기본정보 페이지 내에 건물/호실 테이블 */}
                <div className="contents-container__grid-contents">
                  <BuildingAndHoTable
                    viewTable={buildingAndHo ?? []}
                    onSelectedRowsChange={setSelectedRow}
                    selectedRow={selectedRow}
                    readOnly={true}
                    detailColumn={columns}
                  />
                </div>
              </>
            )}

            <div className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p className="">상품명</p>
              </div>
              <div className="contents-container__grid-contents">
                <div className="minmax400">
                  <span className="font14">{productDetail.productName ?? "-"}</span>
                </div>
              </div>
            </div>

            <div className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p className="">1줄 소개</p>
              </div>
              <div className="contents-container__grid-contents">
                <div className="minmax400">
                  <span className="font14">{productDetail.introduce ?? "-"}</span>
                </div>
              </div>
            </div>

            <section className="contents-container__grid">
              <div className="contents-container__grid-index">
                <div className="flex-center">
                  <p className="">공개 유형</p>
                  <BaseTooltip
                    touchIcon="QUESTION"
                    children={
                      <div>
                        TaapSpace 공개 여부를 설정합니다.
                        <ul className="base-list">
                          <li className="font13 text-primary">공개 안 함 : 누구에게도 공개하지 않습니다.</li>
                          <li className="font13 text-primary">내부 이용자 공개 : 기존 공간임대 상품을 계약하여 이용 중인 이용자에게만 공개합니다.</li>
                          <li className="font13 text-primary">링크 공개 : 아래의 상품 링크로 접근 시에만 공개합니다.</li>
                          <li className="font13 text-primary">전체 공개 : 모든 사용자에게 제한 없이 공개합니다.</li>
                        </ul>
                      </div>
                    }
                  />
                </div>
              </div>
              <div className="contents-container__grid-contents">
                <div className="minmax400">
                  <span className="font14">{productStatusOptions.find((option) => option.value === productDetail.status)?.label || ""}</span>
                </div>
              </div>
            </section>
            {/* {productDetail.status !== "DISABLED" && ( */}
            <div className="bg-light-gray pa20 mb10" style={{ borderRadius: "10px", width: 1070 }}>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p>판매 방식</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div>
                    <span>{salePeriodTypeOptions.find((option) => option.value === productDetail.salePeriodType)?.label}</span>
                  </div>
                </div>
              </section>
              <div className="flex-center">
                <section className="contents-container__grid" style={{ width: 545 }}>
                  <div className="contents-container__grid-index">
                    <p>판매 시작일시</p>
                  </div>
                  <div className="contents-container__grid-contents">
                    <div className="minmax400">
                      <span>{productDetail.saleStartDate ? moment(productDetail.saleStartDate).format(YmdFormat.YYYY_MM_DD_HH_MM) : "-"}</span>{" "}
                    </div>
                  </div>
                </section>

                <section className="contents-container__grid">
                  <div className="contents-container__grid-index">
                    <p>판매 종료일시</p>
                  </div>
                  <div className="contents-container__grid-contents ">
                    <div className="minmax400">
                      <span>{productDetail.saleEndDate ? moment(productDetail.saleEndDate).format(YmdFormat.YYYY_MM_DD_HH_MM) : "-"}</span>{" "}
                    </div>
                  </div>
                </section>
              </div>
              <div className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="">상품 링크</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax400">
                    {productDetail.status !== "DISABLED" ? (
                      <span className="font14 w-100 flex-center-start">
                        {t_product_url}
                        <span className="ml10 text-underline cursor-pointer" title="복사" onClick={handleCopyClipBoard}>
                          복사
                        </span>
                      </span>
                    ) : (
                      <span className="font14">-</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* )} */}

            {/* 공간예약 상품일 때만 노출 */}
            {productDetail.productType === "TIME_COURT" && (
              <>
                <div className="contents-container__grid">
                  <div className="contents-container__grid-index">
                    <p>승인유형</p>
                  </div>
                  <div className="contents-container__grid-contents">
                    <div className="minmax400 flex-row flex-center-start">
                      <span className="font14">{productDetail.approvalType === "AUTO" ? "자동승인" : "관리자승인"}</span>
                    </div>
                  </div>
                </div>
                <div className="contents-container__grid">
                  <div className="contents-container__grid-index">
                    <p className="">이용신청서</p>
                  </div>
                  <div className="contents-container__grid-contents">
                    <div className="minmax400">
                      <div className="flex-center">
                        <span>{productDetail.isUseApplication ? "사용" : "미사용"}</span>
                        {isAuthority("w") && (
                          <BaseButton
                            title="설정"
                            className="color-white ml10"
                            onClick={openApplicationModal}
                            disabled={productDetail.productType !== "TIME_COURT"}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            {productDetail.productType === ProductTypes.TIME_COURT && (
              <>
                <section className="contents-container__grid">
                  <div className="contents-container__grid-index">
                    <p className="">온라인 결제</p>
                  </div>
                  <div className="contents-container__grid-contents">
                    <div className="minmax400">
                      <span className="font14">{productDetail.isOnlinePayment ? "사용" : "미사용"}</span>
                    </div>
                  </div>
                </section>
                <section className="contents-container__grid">
                  <div className="contents-container__grid-index">
                    <p className="">결제 가능 수단</p>
                  </div>
                  <div className="contents-container__grid-contents">
                    <div className="minmax400">
                      <span className="font14">{getPgcodeLabel(productDetail.pgcodeOption)}</span>
                    </div>
                  </div>
                </section>
                <div className="bg-light-gray pa20 mb10" style={{ borderRadius: "10px", width: 1070 }}>
                  <section className="contents-container__grid mb0">
                    <div className="contents-container__grid-index">
                      <p className="">결제기한</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <div className="minmax400">
                        <span className="font14">{paymentDueOffsetText}</span>
                      </div>
                    </div>
                  </section>
                </div>
                <section className="contents-container__grid">
                  <div className="contents-container__grid-index">
                    <p className="">이용자 취소 가능 여부</p>
                  </div>
                  <div className="contents-container__grid-contents">
                    <div className="minmax400">
                      <span className="font14">{productDetail.isMemberCancelable ? "취소 가능" : "취소 불가"}</span>
                    </div>
                  </div>
                </section>
                <div className="bg-light-gray pa20 mb10" style={{ borderRadius: "10px", width: 1070 }}>
                  <section className="contents-container__grid mb0">
                    <div className="contents-container__grid-index">
                      <p className="">이용시작일시</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <div className="minmax400">
                        <span className="font14">{memberCancelableOffsetText}</span>
                      </div>
                    </div>
                  </section>
                </div>
              </>
            )}
            <div className="contents-container__grid">
              <div className="contents-container__grid-index">
                <p className="">계약 가능 여부</p>
              </div>
              <div className="contents-container__grid-contents">
                <div className="minmax400 flex-row flex-center-start">
                  <span className="font14">{productDetail.isContractAvail ? "가능" : "불가능"}</span>
                  {isAuthority("w") && (
                    <BaseButton
                      className="color-white ml10"
                      title="변경"
                      onClick={() =>
                        setBaseModal({
                          isOpen: true,
                          children: <ContractAvailModal productId={productId} isContractAvailDefault={productDetail.isContractAvail ?? false} />,
                        })
                      }
                    ></BaseButton>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <RegistrationInformation data={productDetail} />

        <AdminMemoV3 serviceId={Number(productId)} serviceType={ServiceTypes.SERVICE_PRODUCT}></AdminMemoV3>
      </div>
      {/* 버튼영역 */}
      <div className="contents-container__btn-wrap">
        <div className="left-area d-flex">
          <GoToListButton />
        </div>
        <div className="right-area d-flex">
          {isAuthority("d") && (
            <BaseButton
              title="삭제"
              className="color-white size-large mr5"
              onClick={() =>
                setDeleteModal({
                  isOpen: true,
                  message: "삭제하시겠습니까?",
                  type: "PRODUCT_DELETE",
                })
              }
            />
          )}
          {isAuthority("w") && (
            <BaseButton
              title="수정"
              className="size-large"
              onClick={() => {
                const formPath = `${PagePath.product.form}?id=${productId}`;
                navigate(formPath);
              }}
            />
          )}
        </div>
      </div>

      <BaseModal
        isOpen={deleteModal.isOpen}
        btnLeftTitle={"취소"}
        btnRightTitle="확인"
        title={deleteModal.message}
        onClose={() => setDeleteModal({ isOpen: false })}
        onClick={() => onDeleteProductDetail()}
      />
    </>
  );
};

export default BasicInfoDetail;
